import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as Styles from './CloudFlareHandler.styles';
import { useHistory, useParams } from 'react-router';
import useLocale from '@/hooks/useLocale';
import CloudFlareHeader from './components/CloudFlareHeader';
import DomainItem from './components/DomainItem';
import { useDispatch, useSelector } from 'react-redux';
import { faqActions, sitesActions } from '@/redux/modules';
import {
  Button, Icon404File, IconLoading, IconSearchFail,
  Skeleton,
} from 'gatorcomponents';
import { cloudflareActions } from '@/redux/modules/cloudflare';
import { ChangePlanModal } from './components/ChangePlanModal';
import { WhatIsCdnModal } from './components/WhatIsCdnModal';
import { SUPPORT_DOMAINS_URL } from '@/config/urls/domainsUrls';
import FaqDefault from '@/components/FrequentlyQuestions/FaqDefault';
import { ORIGIN, STATUS } from './CloudFlareHandler.types';
import { getCookie, setCookie } from '@/utils/Application/cookies';
import ActivateDeactivateInternalDomainModal from './components/ActivateDeactivateInternalDomainModal';
import UseDnsModal from '@/hooks/dns/useDnsModal/';
import { SELECT_MODAL_STEPS } from '@/hooks/dns/useDnsModal/useDnsModal.types';

let filterTimeout = null;
export const CloudFlareHandler = () => {
  const { sites, cloudflare } = useSelector(state => state);
  const { list: listItems } = cloudflare;
  const { hostingId } = useParams();
  const dispatch = useDispatch();
  const { cloudflare: cloudflareLocale, routes: routesLocale } = useLocale();
  const [inputedFilter, setInputedFilter] = useState('');
  const [currentPlan, setCurrentPlan] = useState({ });
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [changePlanModal, enableChangePlanModal] = useState(false);
  const [changePlanSelectedOption, setChangePlanSelectedOption] = useState(null);
  const faq = useSelector(state => state.newFaq);
  const [filteredItems, setFilteredItems] = useState(listItems);
  const [activateDeactivateInternalDomainModal, setActivateDeactivateInternalDomainModal] = useState({});
  const [activateExternalDomainModal, setActivateExternalDomainModal] = useState({});
  const [whatIsCdnModal, setWhatIsCndModal] = useState(false);
  const isolatedRequestDomainFunctionRef = useRef();

  const history = useHistory();

  const handleInternalDomainWithExternalProviderStatus = useCallback(() => {
    const { current: isolatedRequestDomain } = isolatedRequestDomainFunctionRef;

    if (activateDeactivateInternalDomainModal.domain && isolatedRequestDomain) {
      isolatedRequestDomain(activateDeactivateInternalDomainModal.domain);
      setActivateExternalDomainModal(activateDeactivateInternalDomainModal);
      setActivateDeactivateInternalDomainModal({});
    }
  }, [activateDeactivateInternalDomainModal, isolatedRequestDomainFunctionRef]);

  const verifyInternalDomainModalStatusChange = useCallback(() => {
    const domainItem = !!listItems.length && listItems.find(item => item.domain === activateExternalDomainModal.domain);
    const domainOriginHasChange = activateExternalDomainModal && domainItem && domainItem.domain_origin !== activateExternalDomainModal.domain_origin;

    if (domainOriginHasChange && cloudflare.activateCdnStatus === 'success') {
      setActivateExternalDomainModal(domainItem);
    }
  }, [activateExternalDomainModal, listItems, cloudflare.activateCdnStatus]);

  useEffect(() => {
    verifyInternalDomainModalStatusChange();
  }, [verifyInternalDomainModalStatusChange]);

  const handleActivateDomain = useCallback(() => {
    let skipRequest = activateExternalDomainModal;

    if (!!cloudflare.lastActivation.domain && (cloudflare.lastActivation.domain === skipRequest.domain)) {
      skipRequest = cloudflare.lastActivation;
    }

    dispatch(cloudflareActions.cdn.activateCdn({
      hostingId: currentPlan.hosting_id,
      domain: activateDeactivateInternalDomainModal.domain || activateExternalDomainModal.domain,
      domainOrigin: activateDeactivateInternalDomainModal.domain_origin || activateExternalDomainModal.domain_origin,
      skipRequest,
      handleInternalDomainWithExternalProviderStatus,
    }));
  }, [
    activateDeactivateInternalDomainModal.domain,
    activateDeactivateInternalDomainModal.domain_origin,
    currentPlan,
    activateExternalDomainModal,
    dispatch,
    handleInternalDomainWithExternalProviderStatus,
    cloudflare.lastActivation,
  ]);

  const clearCloudFlareLastActivation = useCallback(() => {
    dispatch(cloudflareActions.cdn.setActivatedDomain({}));
  }, [dispatch]);

  const clearDomainDnsStatus = useCallback(() => {
    dispatch(sitesActions.domainDns.resetStatus());
  }, [dispatch]);

  const handleDnsModalClose = () => {
    setActivateExternalDomainModal(false);
  };

  const {
    selectManualDnsModal,
    setSelectManualDnsStep,
    domainDns,
  } = UseDnsModal({
    domain: activateExternalDomainModal.domain,
    originalNs1: activateExternalDomainModal.original_ns1,
    originalNs2: activateExternalDomainModal.original_ns2,
    onClose: handleDnsModalClose,
    displayActivateErrorModal: setActivateDeactivateInternalDomainModal,
    handleActivateDomain,
    cloudflare,
    sites,
    clearCloudFlareLastActivation,
    lastActivation: cloudflare.lastActivation,
  });

  const gotDomainDns = useCallback(() => {
    if (domainDns) {
      setSelectManualDnsStep(SELECT_MODAL_STEPS.GET_DOMAIN_ORIGINAL_REGISTRATION);
    }
  }, [domainDns, setSelectManualDnsStep]);

  useEffect(() => {
    gotDomainDns();
  }, [gotDomainDns]);

  const handleDeactivateDomain = useCallback(() => {
    dispatch(cloudflareActions.cdn.deactivateCdn({
      hostingId: currentPlan.hosting_id,
      domain: activateDeactivateInternalDomainModal.domain,
      domainOrigin: activateDeactivateInternalDomainModal.domain_origin,
    }));
  }, [
    activateDeactivateInternalDomainModal.domain,
    activateDeactivateInternalDomainModal.domain_origin,
    currentPlan,
    dispatch,
  ]);

  const requestSites = useCallback(() => {
    dispatch(sitesActions.sites.request());
    dispatch(faqActions.faq.request.faqCloudflare());
  }, [dispatch]);

  useEffect(() => {
    requestSites();
  }, [requestSites]);

  const setDefaultFilteredListItems = useCallback(() => {
    if (!inputedFilter) {
      setFilteredItems(listItems);
    }
  }, [inputedFilter, listItems]);

  useEffect(() => {
    setDefaultFilteredListItems();
  }, [setDefaultFilteredListItems]);

  const filterCards = useCallback(() => {
    let newFilteredItems = listItems;

    if (inputedFilter) {
      newFilteredItems = [...listItems].filter(item => item.domain.toLowerCase().includes(inputedFilter.toLowerCase()));
    }

    setFilteredItems(newFilteredItems);
  }, [
    inputedFilter,
    listItems,
  ]);

  const handleFilter = useCallback(() => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
      filterCards();
    }, 1000);
  }, [filterCards]);

  useEffect(() => {
    handleFilter();
  }, [handleFilter]);

  const requestDomainsPerHostingId = useCallback(() => {
    if (currentPlan && currentPlan.hosting_id) {
      dispatch(
        cloudflareActions.domains.domainsRequest({ hostingId: currentPlan.hosting_id }),
      );
    }

    if (!hostingId && sites.list.length) {
      const site = sites.list.find(item => item.cloudflare_available && item.status.toLowerCase() === 'active');
      history.push(`${routesLocale.products}${routesLocale.cloudflare}/${site.hosting_id}`);
    }
  }, [
    currentPlan,
    dispatch,
    hostingId,
    sites.list,
    history,
    routesLocale,
  ]);

  useEffect(() => {
    requestDomainsPerHostingId();
  }, [requestDomainsPerHostingId]);

  const getUrlPlanData = useCallback(() => {
    if (sites.list.length && hostingId) {
      const site = sites.list.find(item => item.hosting_id === Number(hostingId));
      site && setCurrentPlan(site);
    }
  }, [sites, hostingId]);

  useEffect(() => {
    getUrlPlanData();
  }, [getUrlPlanData]);

  const getCloudFlareHeaderProps = useCallback(() => {
    const handleChangePlan = () => {
      enableChangePlanModal(true);
    };

    const verifyRefreshTimeLimit = () => {
      const lastRefresh = Number(getCookie('last-cloudflare-refresh'));
      const currentTimestamp = Date.now();

      const timeLimit = 60 * 60 * 1000;

      const timeDifferenceInHours = (currentTimestamp - lastRefresh) / timeLimit;

      if (!lastRefresh) {
        return true;
      }
      if (timeDifferenceInHours >= 1) {
        return true;
      }
      return false;
    };

    const setLastRefreshOnCookie = (timestamp) => {
      setCookie('last-cloudflare-refresh', timestamp);
    };

    const hasOpenStatus = listItems.find(item => (
      item.status === STATUS.PENDING_DEACTIVATION
      || item.status === STATUS.PENDING
      || item.status === STATUS.DELAY
      || (item.status === STATUS.NONEXISTENT && item.origin === ORIGIN.EXTERNAL)
    ));

    const handleWhatIsCdn = () => {
      setWhatIsCndModal(true);
    };

    return ({
      hostingLabel: cloudflareLocale.hostingLabel,
      changeCta: cloudflareLocale.change,
      cloudflareCdnExplanation: cloudflareLocale.cloudflareCdnExplanation({ handleWhatIsCdn }),
      searchPerDomainLabel: cloudflareLocale.searchPerDomain,
      refreshStatusLabel: cloudflareLocale.refreshStatus,
      noOpenStatusLabel: cloudflareLocale.noOpenStatus,
      waitRefreshTimeLimitLabel: cloudflareLocale.waitRefreshTimeLimit,
      cloudflareStatusLoading: cloudflare.loading,
      currentPlan: currentPlan.product_name || '',
      currentDomain: currentPlan.domain || '',
      inputedFilter,
      sites: sites.list.filter(item => item.cloudflare_available && item.status.toLowerCase() === 'active'),
      hasOpenStatus,
      refreshTimeLimitHasEnded: verifyRefreshTimeLimit(),
      handleRefreshStatus: requestDomainsPerHostingId,
      setLastRefreshOnCookie,
      setInputedFilter,
      handleChangePlan,
      title: cloudflareLocale.title,
    });
  }, [
    cloudflareLocale,
    inputedFilter,
    setInputedFilter,
    requestDomainsPerHostingId,
    cloudflare.loading,
    currentPlan,
    listItems,
    sites,
  ]);

  const getChangePlanModal = useCallback(() => {
    const handleSetPlan = (plan) => {
      setCurrentPlan(plan);
      history.push(`${routesLocale.products}${routesLocale.cloudflare}/${plan.hosting_id}`);
    };

    return {
      sites: sites.list.filter(item => item.cloudflare_available && item.status.toLowerCase() === 'active'),
      onClose: () => enableChangePlanModal(false),
      currentPlan,
      handleSetPlan,
      backCta: cloudflareLocale.changePlanModal.back,
      changePlanSelectedOption,
      setChangePlanSelectedOption,
      continueCta: cloudflareLocale.changePlanModal.continue,
      title: cloudflareLocale.changePlanModal.title,
      searchPlaceholder: cloudflareLocale.changePlanModal.searchPlaceholder,
    };
  }, [
    sites.list,
    changePlanSelectedOption,
    setChangePlanSelectedOption,
    currentPlan,
    cloudflareLocale,
    history,
    routesLocale,
  ]);

  const getDomainItemProps = useCallback(({ item }) => {
    let nameServerTooltipContent = null;
    if (item.cloudflare_ns1 && item.cloudflare_ns2) {
      nameServerTooltipContent = cloudflareLocale.nameServerTooltip(item.cloudflare_ns1, item.cloudflare_ns2);
    }

    const handleVerifyDomain = () => {
      setActivateExternalDomainModal(item);
    };

    const manageDomainCdn = () => {
      if (item.cloudflare_internal_id) {
        history.push(`${routesLocale.products}${routesLocale.cloudflare}${routesLocale.domaindetail}/${item.cloudflare_internal_id}`);
      }
    };

    const handleSetActivateDeactivateInternalDomainModal = () => {
      setActivateDeactivateInternalDomainModal(item);
    };

    return {
      domain: item.domain,
      status: item.status,
      origin: item.domain_origin,
      activateCdn: cloudflareLocale.activateCdn,
      manageLabel: cloudflareLocale.manage,
      messageOngoingActivation: cloudflareLocale.ongoingActivation,
      messageOngoingExternalActivation: cloudflareLocale.ongoingExternalActivation,
      messageOngoingDeactivation: cloudflareLocale.ongoingDeactivation,
      messageExternalDomain: cloudflareLocale.externalDomain,
      nameServerTooltipContent,
      statusLabel: {
        nonexistentexternal: cloudflareLocale.status.nonexistentexternal,
        pendinginternal: cloudflareLocale.status.pendinginternal,
        pendingexternal: cloudflareLocale.status.pendingexternal,
        delayinternal: cloudflareLocale.status.delayinternal,
        delayexternal: cloudflareLocale.status.delayexternal,
        pendingdeactivationinternal: cloudflareLocale.status.pendingdeactivationinternal,
        pendingdeactivationexternal: cloudflareLocale.status.pendingdeactivationexternal,
        activationpointingerrorinternal: cloudflareLocale.status.activationpointingerrorinternal,
        activationpointingerrorexternal: cloudflareLocale.status.activationpointingerrorexternal,
        inactivationpointingerrorinternal: cloudflareLocale.status.inactivationpointingerrorinternal,
        inactivationpointingerrorexternal: cloudflareLocale.status.inactivationpointingerrorexternal,
        errorinternal: cloudflareLocale.status.error,
        errorexternal: cloudflareLocale.status.error,
        externalproviderinternal: cloudflareLocale.status.externalproviderinternal,
      },
      pendingTooltip: cloudflareLocale.pendingTooltip,
      cdnAlreadyActive: cloudflareLocale.cdnAlreadyActive,
      onClickToggle: handleSetActivateDeactivateInternalDomainModal,
      onVerifyExternalDomain: handleVerifyDomain,
      manageDomainCdn,
      messageWithActivationError: cloudflareLocale.messageWithActivationError,
      messageWithDeactivationError: cloudflareLocale.messageWithDeactivationError,
    };
  }, [
    cloudflareLocale,
    history,
    routesLocale,
  ]);

  const getActivateDeactivateInternalDomainModalProps = useCallback(() => {
    const handleCloseModal = () => {
      clearDomainDnsStatus();
      clearCloudFlareLastActivation();
      setActivateDeactivateInternalDomainModal({});
    };

    return ({
      onClose: handleCloseModal,
      onContinueActivating: handleActivateDomain,
      onContinueDeactivating: handleDeactivateDomain,
      toActivateTitle: cloudflareLocale.activateDeactivateInternalModal.toActivateTitle,
      toDeactivateTitle: cloudflareLocale.activateDeactivateInternalModal.toDeactivateTitle,
      deactivatingTitle: cloudflareLocale.activateDeactivateInternalModal.deactivatingTitle,
      activatingTitle: cloudflareLocale.activateDeactivateInternalModal.activatingTitle,
      toActivateDescription: cloudflareLocale.activateDeactivateInternalModal.toActivateDescription,
      activatingDescription: cloudflareLocale.activateDeactivateInternalModal.activatingDescription,
      deactivatingDescription: cloudflareLocale.activateDeactivateInternalModal.deactivatingDescription,
      toDeactivateDescription: cloudflareLocale.activateDeactivateInternalModal.toDeactivateDescription,
      cancelCta: cloudflareLocale.activateDeactivateInternalModal.cancelCta,
      confirmationCta: cloudflareLocale.activateDeactivateInternalModal.confirmationCta,
      continueActivationCta: cloudflareLocale.activateDeactivateInternalModal.continueActivationCta,
      continueDeactivationCta: cloudflareLocale.activateDeactivateInternalModal.continueDeactivationCta,
      failedRequestDescription: cloudflareLocale.activateDeactivateInternalModal.failedRequestDescription,
      failedRequestTitle: cloudflareLocale.activateDeactivateInternalModal.failedRequestTitle,
      domainWithProblemTitle: cloudflareLocale.activateDeactivateInternalModal.domainWithProblemTitle,
      domainWithProblemDescription: cloudflareLocale.activateDeactivateInternalModal.domainWithProblemDescription,
      currentStatus: activateDeactivateInternalDomainModal.status,
      origin: activateDeactivateInternalDomainModal.domain_origin,
      domain: activateDeactivateInternalDomainModal.domain,
      loadingActivation: cloudflare.loadingActivation,
      lastActivation: cloudflare.lastActivation,
    });
  }, [
    activateDeactivateInternalDomainModal,
    cloudflare.loadingActivation,
    cloudflareLocale,
    handleActivateDomain,
    cloudflare.lastActivation,
    clearCloudFlareLastActivation,
    handleDeactivateDomain,
    clearDomainDnsStatus,
  ]);

  const getWhatIsCndModalProps = useCallback(() => ({
    onClose: () => setWhatIsCndModal(false),
    title: cloudflareLocale.whatIsCdnModal.title,
    description: cloudflareLocale.whatIsCdnModal.description,
    items: cloudflareLocale.whatIsCdnModal.items,
    cta: cloudflareLocale.whatIsCdnModal.cta,
  }), [cloudflareLocale]);

  if (
    sites.loadingSites
    || cloudflare.loading
  ) {
    return (
      <Styles.Container data-testid="CloudFlareHandlerContainer">
        <Styles.SkeletonItem>
          <Styles.SkeletonHeaderColumn>
            <Skeleton variant="headline" width="268px" />
            <Skeleton variant="textLine" width="609px" />
            <Skeleton variant="textLine" width="609px" />
          </Styles.SkeletonHeaderColumn>

          <Skeleton variant="rectangle" width="320px" height="56px" />
        </Styles.SkeletonItem>

        <Styles.ListWrapper>
          {Array.from(Array(10).keys()).map(item => (
            <Styles.SkeletonItem key={`skeleton_${item}`}>
              <Skeleton variant="paragraph" width="660px" />

              <Styles.SkeletonButton>
                <Skeleton variant="button" />
                <Skeleton variant="button" />
              </Styles.SkeletonButton>
            </Styles.SkeletonItem>
          ))}
          <IconLoading />
        </Styles.ListWrapper>
      </Styles.Container>
    );
  }

  return (
    <>
      {whatIsCdnModal && (
        <WhatIsCdnModal
          {...getWhatIsCndModalProps()}
          onClose={() => setWhatIsCndModal(false)}
        />
      )}

      {activateExternalDomainModal && activateExternalDomainModal.domain && (
        selectManualDnsModal()
      )}

      {changePlanModal && (
        <ChangePlanModal
          {...getChangePlanModal()}
        />
      )}

      {activateDeactivateInternalDomainModal && activateDeactivateInternalDomainModal.domain && (
        <ActivateDeactivateInternalDomainModal
          {...getActivateDeactivateInternalDomainModalProps()}
        />
      )}

      <Styles.Container data-testid="CloudFlareHandlerContainer">
        <CloudFlareHeader {...getCloudFlareHeaderProps()} />

        <Styles.ListWrapper>
          {!filteredItems.length && (
            <Styles.EmptyCard data-testid="emptyState">
              {cloudflare.domainsError
                ? <Icon404File size="xl" color="lowLight" />
                : <IconSearchFail size="xl" color="lowLight" />
              }

              <Styles.NoDomainText>
                {cloudflare.domainsError
                  ? cloudflareLocale.domainsError
                  : cloudflareLocale.noDomains}
              </Styles.NoDomainText>

              {cloudflare.domainsError && (
                <Styles.RefreshButtonRow>
                  <Button
                    label={cloudflareLocale.refreshList}
                    onClick={requestDomainsPerHostingId}
                    size="large"
                  />
                </Styles.RefreshButtonRow>
              )}
            </Styles.EmptyCard>
          )}

          {filteredItems.slice(0, (itemsPerPage * page)).map(item => (
            <DomainItem
              key={`${item.hostingId}_${item.domain}`}
              {...getDomainItemProps({ item })}
            />
          ))}
        </Styles.ListWrapper>

        {(filteredItems.length > (itemsPerPage * page)) && (
          <Styles.ButtonRow>
            <Button
              testId="show-more-button"
              variant="secondary"
              label={cloudflareLocale.showMore}
              onClick={() => setPage(page + 1)}
            />
          </Styles.ButtonRow>
        )}

        <Styles.FaqRow>
          <FaqDefault
            questions={faq.cloudflare.questions}
            link={SUPPORT_DOMAINS_URL}
            loading={faq.loading}
          />
        </Styles.FaqRow>
      </Styles.Container>
    </>
  );
};

export default CloudFlareHandler;
